import React, { useState } from "react";
import { Button, HelperText, Label, Spinner, TextInput } from "flowbite-react";
import { useTranslation, useToast } from "@repo/core";
import { useChangePassword } from "@repo/api";
import PasswordChecklist from "../../components/auth/PasswordChecklist";
import { passwordMeetsAllRules } from "../../utils/passwordRules";
import { extractChangePasswordFieldErrors } from "../../utils/authErrors";
import type { ChangePasswordFieldErrors } from "../../utils/authErrors";

/** Password change form, mirroring anp-fe's ChangePasswordForm.vue: the same
 * three fields, the same client-side rules, and the same "show the backend's
 * per-field errors inline" behaviour. */
const ChangePasswordCard: React.FC = () => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<ChangePasswordFieldErrors>({});

  const { t } = useTranslation();
  const { addSuccessMessage, addErrorMessage } = useToast();
  const changePassword = useChangePassword();

  const passwordsMatch = newPassword === confirmPassword;
  const canSubmit =
    oldPassword.length > 0 &&
    passwordMeetsAllRules(newPassword) &&
    passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    try {
      await changePassword.mutateAsync({
        old_password: oldPassword,
        new_password1: newPassword,
        new_password2: confirmPassword,
      });

      addSuccessMessage(t("profile.password.success"));
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("Password change failed:", error);
      const errors = extractChangePasswordFieldErrors(error);
      if (Object.values(errors).some(Boolean)) {
        setFieldErrors(errors);
      }
      // Always toast as well - anp-fe does the same, so a failure is visible
      // even when the error lands on a field that's scrolled out of view.
      addErrorMessage(t("profile.password.error"));
    }
  };

  return (
    <section className="rounded-lg bg-white p-8 shadow dark:bg-gray-800">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
        {t("profile.password.title")}
      </h3>
      <p className="mt-1 mb-6 text-sm text-gray-600 dark:text-gray-400">
        {t("profile.password.description")}
      </p>

      <form className="space-y-4" onSubmit={handleSubmit}>
        <div>
          <Label htmlFor="oldPassword">{t("profile.password.currentLabel")}</Label>
          <TextInput
            id="oldPassword"
            name="oldPassword"
            type="password"
            autoComplete="current-password"
            required
            color={fieldErrors.oldPassword ? "failure" : undefined}
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
          />
          {fieldErrors.oldPassword && (
            <HelperText color="failure">{fieldErrors.oldPassword}</HelperText>
          )}
        </div>

        <div>
          <Label htmlFor="newPassword">{t("profile.password.newLabel")}</Label>
          <TextInput
            id="newPassword"
            name="newPassword"
            type="password"
            autoComplete="new-password"
            required
            color={fieldErrors.newPassword ? "failure" : undefined}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          {fieldErrors.newPassword && (
            <HelperText color="failure">{fieldErrors.newPassword}</HelperText>
          )}
          <PasswordChecklist password={newPassword} />
        </div>

        <div>
          <Label htmlFor="confirmPassword">{t("profile.password.confirmLabel")}</Label>
          <TextInput
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            required
            color={
              fieldErrors.confirmPassword ||
              (confirmPassword.length > 0 && !passwordsMatch)
                ? "failure"
                : undefined
            }
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          {(fieldErrors.confirmPassword ||
            (confirmPassword.length > 0 && !passwordsMatch)) && (
            <HelperText color="failure">
              {fieldErrors.confirmPassword ?? t("profile.password.mismatch")}
            </HelperText>
          )}
        </div>

        <Button
          type="submit"
          color="red"
          className="w-full"
          disabled={changePassword.isPending || !canSubmit}
        >
          {changePassword.isPending && <Spinner size="sm" className="mr-2" light />}
          {t("profile.password.submit")}
        </Button>
      </form>
    </section>
  );
};

export default ChangePasswordCard;
